
import TestPart from '@/models/TestPart';
import { Prop, Vue, Watch } from 'vue-property-decorator';
import { inject } from 'vue';
import PlayerEmitter from '../../event/PlayerEmitter';
import EventBusConstants from '@/constants/EventBusConstants';
import Section from '@/models/Section';
import Item from '@/models/Item';
import DisplayItem from '@/models/DisplayItem';
import ReviewSection from '@/models/ReviewSection';
import InstructionalItem from '@/models/InstructionalItem';
import ITestReviewDisplayStrategy from './ITestReviewDisplayStrategy';
import TestReviewDisplayStrategy from './TestReviewDisplayStrategy';
import TestReviewScreenDisplayStrategy from './TestReviewScreenDisplayStrategy';
import INavigationController from '@/controllers/navigation/INavigationController';
import Test from '@/models/Test';
import DeliveryOptionConstants from '@/controllers/options/DeliveryOptionConstants';
import DeliveryOptionUtility from '@/controllers/options/DeliveryOptionUtility';
import ItemStatus from '@/status/ItemStatus';
import Utility from '@/util/Utility';
import AlertUtility from '@/util/AlertUtility';

export default class TestReview extends Vue {
  @Prop() readonly navigationController: INavigationController | undefined = undefined;
  @Prop() readonly testPart: TestPart | undefined;
  @Prop() readonly displayItems: Array<DisplayItem> | undefined;
  @Prop({ default: false }) displayAsScreen: boolean | undefined;
  @Prop({ default: false }) sectionLockEnabled: boolean | undefined;
  @Prop({ default: false }) isTestEndReview: boolean | undefined;
  @Prop({ default: false }) flagItemEnabled: boolean | undefined;

  private hidden_ = true;
  private testReviewDisplayStrategy: ITestReviewDisplayStrategy = new TestReviewDisplayStrategy();

  private displayHeight_ = '0px';
  private styleObject_: Map<string, string> = new Map<string, string>();
  private playerEmitter: PlayerEmitter | undefined = inject('playerEmitter');

  private filterType_ = 'all';
  private textAvailable_ = false;
  private itemPositionMap: Map<string, number> = new Map<string, number>();
  private useSubmitIcon = false;
  private observer: MutationObserver | undefined = undefined;
  private get hidden() {
    return this.hidden_;
  }

  private waitForProctorReleaseEnabled(): boolean {
    if (this.testPart) {
      return DeliveryOptionUtility.getDeliveryOptionAndCheckExpectedValue(
        this.testPart.deliveryOptions,
        DeliveryOptionConstants.PROCTOR_RELEASE,
        'value',
        'true'
      );
    }
    return false;
  }

  private get filterType(): string {
    return this.filterType_;
  }

  private set filterType(value: string) {
    this.filterType_ = value;
    this.createSectionAndItemElements();
    this.$forceUpdate();
  }

  private createFilteredReviewSections(): Array<ReviewSection> {
    if (this.displayItems?.length == 0) {
      return new Array<ReviewSection>();
    }

    let currentSection: Section | null = null;
    let reviewSection: ReviewSection | null = null;

    let reviewSections: Array<ReviewSection> = new Array<ReviewSection>();

    this.getAdjustedDisplayItems()!.forEach((displayItem) => {
      if (displayItem.section != currentSection) {
        currentSection = displayItem.section;
        reviewSection = new ReviewSection(currentSection!.identifier, currentSection!.title, currentSection);
        reviewSections.push(reviewSection);
      }

      let addItem = true;
      if (this.filterType !== 'all') {
        addItem = false;
        if (displayItem instanceof InstructionalItem) {
          addItem = false;
        } else if (displayItem instanceof Item) {
          const item: Item = displayItem as Item;
          if (this.filterType === 'fullyAttempted') {
            addItem = item.itemStatus === ItemStatus.FULLY_ATTEMPTED;
          } else if (this.filterType === 'partiallyAttempted') {
            addItem = item.itemStatus === ItemStatus.PARTIALLY_ATTEMPTED;
          } else if (this.filterType === 'unattempted') {
            addItem = item.itemStatus === ItemStatus.UNATTEMPTED;
          } else if (this.filterType === 'flagged') {
            addItem = item.isFlagged;
          }
        }
      }

      if (addItem) {
        reviewSection?.addDisplayItem(displayItem);
      }
    });

    return reviewSections.filter((section) => section.displayItems.length > 0);
  }

  private createSectionAndItemElements(): void {
    const filteredReviewSections: Array<ReviewSection> = this.createFilteredReviewSections();
    const innerQuestionContainer: HTMLElement | null = this.$el.querySelector('#inner-question-container');
    if (innerQuestionContainer) {
      Array.from(innerQuestionContainer.getElementsByClassName('section-container')).forEach((element) => {
        element.remove();
      });
    }

    if (!innerQuestionContainer) {
      return;
    }

    for (let sectionIndex = 0; sectionIndex < filteredReviewSections.length; sectionIndex++) {
      const section: ReviewSection = filteredReviewSections[sectionIndex];

      const sectionContainer: Element = this.createElement('div', ['section-container']);
      innerQuestionContainer!.append(sectionContainer);

      const leftSideContainer: Element = this.createElement('div', ['left-side-container']);
      sectionContainer.append(leftSideContainer);

      const iconContainerClasslist: Array<string> = new Array<string>();
      iconContainerClasslist.push('icon-container');
      if (this.isCurrentSection(section)) {
        iconContainerClasslist.push('current-section');
      }
      const iconContainer: HTMLElement = this.createElement('div', iconContainerClasslist);
      leftSideContainer.append(iconContainer);

      iconContainer.style.marginLeft = this.getSectionMargin();

      iconContainer.append(this.createElement('i', ['fa', 'fa-circle']));

      const verticalLineContainer = this.createElement('div', ['vertical-line-container']);
      leftSideContainer.append(verticalLineContainer);

      if (sectionIndex < filteredReviewSections.length - 1) {
        verticalLineContainer.append(this.createElement('div', ['vertical-line']));
      }

      const rightSideContainer: Element = this.createElement('div', ['right-side-container']);
      sectionContainer.append(rightSideContainer);

      const sectionTitleContainer: Element = this.createElement('div', ['section-title-container']);
      rightSideContainer.append(sectionTitleContainer);

      const sectionTitle = this.createElement('span', ['align-items-center']);
      sectionTitleContainer.append(sectionTitle);
      sectionTitle.innerHTML = section.title;

      const buttonContainer = this.createElement('div', [
        'left-button-container',
        'button-container',
        'd-flex',
        'align-items-center',
      ]);
      rightSideContainer.append(buttonContainer);

      for (let index = 0; index < section.displayItems.length; index++) {
        const item: DisplayItem = section.displayItems[index];

        const buttonClassArray: Array<string> = this.getButtonClass(index, item, section);
        buttonClassArray.push('item-button');

        const button: HTMLElement = this.createElement('button', buttonClassArray);
        buttonContainer.append(button);
        button.title = this.getButtonTitle(item);
        button.addEventListener('click', () => {
          this.handleClick(item);
        });

        const buttonTextContainer: Element = this.createElement('div', ['button-text-container']);
        button.append(buttonTextContainer);

        if (this.isInstructionalItem(item)) {
          buttonTextContainer.append(this.createElement('i', ['info-button', 'fa', 'fa-circle-info']));
        } else {
          const itemNumber: HTMLElement = this.createElement('a', undefined);
          buttonTextContainer.append(itemNumber);
          itemNumber.innerHTML = this.getItemNumber(item.identifier);
        }

        if (this.isCurrentItem(item)) {
          buttonTextContainer.append(this.createElement('div', this.getArrowClass(item)));
        }

        if (item instanceof Item && item.isFlagged) {
          const classArray: Array<string> = new Array<string>();
          classArray.push('flag-div');
          classArray.push('d-flex');
          classArray.push('align-items-center');
          classArray.push('justify-content-center');
          const divClass = this.getFlagDivClass(item);
          if (divClass) {
            classArray.push(divClass);
          }
          const flaggedContainer: Element = this.createElement('div', classArray);
          buttonTextContainer.append(flaggedContainer);
          flaggedContainer.append(this.createElement('i', ['fa', 'fa-flag']));
        }
      }

      if (this.waitForProctorReleaseEnabled() && !section.releasedByProctor) {
        const rightSideContainerLock: HTMLElement = this.createElement('div', ['right-side-container-lock']);
        buttonContainer.append(rightSideContainerLock);
        const i18n = (window as any).i18n.global;
        rightSideContainerLock.title = i18n.t('review.locked');

        const lockIconContainer: Element = this.createElement('div', ['lock-icon-container']);
        rightSideContainerLock.append(lockIconContainer);

        lockIconContainer.append(this.createElement('i', ['fa', 'fa-lock', 'lock-icon']));
      }
    }
    this.adjustButtons();
  }

  private createElement(tagName: string, classList: Array<string> | undefined): HTMLElement {
    const element: HTMLElement = document.createElement(tagName);

    if (element && classList) {
      classList.forEach((value) => {
        if (value) {
          element.classList.add(value);
        }
      });
    }

    return element;
  }

  private get iconBeforeNext(): string {
    return this.useSubmitIcon ? 'fa mr-2 fa-check' : '';
  }

  private get iconAfterNext(): string {
    return !this.useSubmitIcon ? 'fa fa-arrow-right' : '';
  }

  private get styleObject() {
    return this.styleObject_;
  }

  private get displayHeight() {
    return this.displayHeight_;
  }

  private get textAvailable() {
    return this.textAvailable_;
  }

  private get submitButtonText(): string {
    return this.getSubmitButtonText();
  }

  private getSectionMargin(): string {
    return '0px';
  }

  private getText(): string {
    const text = this.testReviewDisplayStrategy.getText(this.getAdjustedDisplayItems());
    this.textAvailable_ = text.length > 0;

    return text;
  }

  private getItemNumber(itemIdentifier: string): string {
    let value = this.itemPositionMap.get(itemIdentifier)?.toString();

    return value ? value : '';
  }

  private isCurrentItem(item: DisplayItem): boolean {
    return (
      !this.testReviewDisplayStrategy.isScreenDisplay() &&
      ((item instanceof InstructionalItem && item.currentItem) ||
        (item instanceof Item &&
          item.currentItem &&
          item.section.testPart.test.locationState !== Test.SECTION_INSTRUCTIONS_STATE))
    );
  }

  private getButtonTitle(item: DisplayItem): string {
    let title = '';
    if (!(item instanceof InstructionalItem)) {

      const i18n = (window as any).i18n.global;

      const itemStatus: ItemStatus = (item as Item).itemStatus;
      if (itemStatus === ItemStatus.UNATTEMPTED) {
        title = i18n.t('review.unattempted');
      } else if (itemStatus === ItemStatus.PARTIALLY_ATTEMPTED) {
        title = i18n.t('review.partial_attempted');
      } else if (itemStatus === ItemStatus.FULLY_ATTEMPTED) {
        title = i18n.t('review.fully_attempted');
      }
    }
    return title;
  }

  private getArrowClass(item: DisplayItem): Array<string> {
    const classArray: Array<string> = new Array<string>();
    classArray.push('arrow-up');

    if (!(item instanceof InstructionalItem)) {
      const regularItem: Item = item as Item;
      if (regularItem.itemStatus === ItemStatus.FULLY_ATTEMPTED) {
        classArray.push('arrow-up-completed');
      }
    }
    return classArray;
  }

  private getButtonClass(index: number, item: DisplayItem, section: ReviewSection): Array<string> {
    const classArray: Array<string> = new Array<string>();

    if (index === 0) {
      classArray.push('first-button');
    }
    if (index === section.displayItems.length - 1) {
      classArray.push('last-button');
    }

    if (this.isCurrentItem(item)) {
      classArray.push('current-item');
    }

    let canRemoveRightBorder = false;
    if (!(item instanceof InstructionalItem)) {
      const regularItem: Item = item as Item;
      if (regularItem.itemStatus === ItemStatus.PARTIALLY_ATTEMPTED) {
        classArray.push('item-visited');
        canRemoveRightBorder = true;
      } else if (regularItem.itemStatus === ItemStatus.FULLY_ATTEMPTED) {
        classArray.push('item-completed');
        canRemoveRightBorder = true;
        if (this.isCurrentItem(item)) {
          classArray.push('current-item-fully-attempted');
        }
      } else if (regularItem.itemStatus == ItemStatus.UNATTEMPTED) {
        classArray.push('item-unattempted');
      }
    }
    if (
      !this.testReviewDisplayStrategy.isScreenDisplay() &&
      canRemoveRightBorder &&
      this.nextItemIsCurrent(item, section)
    ) {
      classArray.push('no-item-button-right-border');
    }

    return classArray;
  }

  private isInstructionalItem(item: DisplayItem) {
    return item instanceof InstructionalItem;
  }

  private getFlagDivClass(item: Item): string | undefined {
    return item.currentItem &&
      item.section.testPart.test.locationState !== Test.SECTION_INSTRUCTIONS_STATE &&
      !this.testReviewDisplayStrategy.isScreenDisplay()
      ? 'flag-div-selected'
      : undefined;
  }

  private isCurrentSection(section: ReviewSection): boolean {
    return (
      this.testReviewDisplayStrategy.isScreenDisplay() ||
      section.displayItems.filter((item) => item.currentItem).length > 0
    );
  }

  private handleClick(item: DisplayItem): void {
    
    if (!AlertUtility.isNavigationAllowed(item)) return

    const currentItem = this.navigationController?.getCurrentItem();
    if (!this.testReviewDisplayStrategy.isScreenDisplay()) {
      if (currentItem && item && currentItem.section != item.section) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const that = this;
        const incompleteCount = currentItem.section.getIncompleteCount();
        const flaggedCount = currentItem.section.getFlaggedCount();

        let message1 = this.$t("modal.questions_complete");
        let message2 = this.$t("modal.continue_confirm");
        const title = this.$t("review.continue");
        if (incompleteCount + flaggedCount > 0) {
          message1 = Utility.getIncompleteAndFlaggedText(incompleteCount, flaggedCount, null, null);
        }

        AlertUtility.displayQuestionDialogWithCustomButtonText(title, message1 + ' ' + message2, this.$t("btn.cancel"), this.$t("btn.continue"))
          .then(() => {
            that.notifyItemPicked(item);
          })
          .catch(() => {
            //Do nothing
          });
      } else {
        this.notifyItemPicked(item);
      }
    } else {
      this.notifyItemPicked(item);
    }
  }

  private notifyItemPicked(item: DisplayItem): void {
    this.playerEmitter!.emit(EventBusConstants.ITEM_PICKED, [
      ['item', item],
      ['sectionReview', this.testReviewDisplayStrategy.isScreenDisplay()],
    ]);
  }

  private submitButtonClicked(): void {
    this.playerEmitter!.emit(EventBusConstants.REVIEW_SUBMIT);
  }

  @Watch('testPart')
  private onPropertyChanged(newValue: TestPart): void {
    this.updateItemPositionMap(newValue);
  }

  created() {
    this.updateItemPositionMap(this.testPart!);
  }

  mounted() {
    this.testReviewDisplayStrategy = this.displayAsScreen
      ? new TestReviewScreenDisplayStrategy()
      : new TestReviewDisplayStrategy();

    window.addEventListener('resize', this.windowSizeChanged);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this.playerEmitter!.on(EventBusConstants.TEST_REVIEW_EVENT, (data: Map<string, unknown>) => {
      this.createSectionAndItemElements();
      this.hidden_ = !this.hidden_;
      this.displayHeight_ = this.testReviewDisplayStrategy.getDisplayHeight(this.hidden_);
      this.$forceUpdate();
    });

    this.styleObject_ = this.testReviewDisplayStrategy.createStyle();
    this.displayHeight_ = this.testReviewDisplayStrategy.getDisplayHeight(this.hidden_);

    this.observer = new MutationObserver(() => {
      this.adjustButtons();
      this.windowSizeChanged();
    });

    const element = document.getElementById('inner-question-container');
    if (element) {
      this.observer.observe(element, {
        childList: true,
        subtree: true,
      });
    }

    this.createSectionAndItemElements();
    this.$forceUpdate();
  }

  unmounted() {
    this.observer?.disconnect();
  }

  private getAdjustedDisplayItems(): Array<DisplayItem> {
    let displayItemArray: Array<DisplayItem> = this.displayItems!;

    if (!this.isTestEndReview || this.sectionLockEnabled) {
      const activeSection: Section = this.testPart!.test.getCurrentItem()!.section;
      displayItemArray = this.displayItems!.filter((item) => item.section == activeSection);
    }

    return displayItemArray;
  }

  private getSubmitButtonText(): string {
    this.useSubmitIcon = true;

    if (this.isTestEndReview && !this.sectionLockEnabled) {
      return this.$t('modal.submit_header');
    } else {
      if (this.navigationController) {
        if (this.navigationController.lastSectionInTest()) {
          return this.$t('modal.submit_header');
        } else {
          const activeSection: Section = this.testPart!.test.getCurrentItem()!.section;
          const currentIndex = this.displayItems!.findIndex((item) => item == this.testPart!.test.getCurrentItem());
          let text = this.$t('review.continue');
          for (let i = currentIndex; i < this.displayItems!.length; i++) {
            if (this.displayItems![i].section != activeSection) {
              this.useSubmitIcon = false;
              text = this.$t('btn.continue_to') + ' ' + this.displayItems![i].section.title;
              break;
            }
          }
          return text;
        }
      } else {
        return this.$t('btn.submit_section');
      }
    }
  }

  private windowSizeChanged() {
    this.adjustButtons();
    this.displayHeight_ = this.testReviewDisplayStrategy.getDisplayHeight(this.hidden_);
  }

  private adjustButtons() {
    const FIRST_BUTTON_CLASS = 'first-button';
    const LAST_BUTTON_CLASS = 'last-button';
    const buttonContainerElements = document.querySelectorAll('.button-container');
    if (buttonContainerElements) {
      for (const buttonContainer of buttonContainerElements) {
        const elements = buttonContainer.querySelectorAll('.item-button');
        if (elements && elements.length > 0) {
          const leftPosition = (elements[0] as HTMLElement).offsetLeft;
          for (let i = 0; i < elements.length; i++) {
            const currentLeftOffset = (elements[i] as HTMLElement).offsetLeft;
            elements[i].classList.add(LAST_BUTTON_CLASS);
            if (i + 1 < elements.length) {
              const nextLeftOffset = (elements[i + 1] as HTMLElement).offsetLeft;
              if (nextLeftOffset < currentLeftOffset) {
                elements[i].classList.add(LAST_BUTTON_CLASS);
              } else {
                elements[i].classList.remove(LAST_BUTTON_CLASS);
              }
            } else {
              elements[i].classList.add(LAST_BUTTON_CLASS);
            }
            if (currentLeftOffset <= leftPosition) {
              elements[i].classList.add(FIRST_BUTTON_CLASS);
            } else {
              elements[i].classList.remove(FIRST_BUTTON_CLASS);
            }
          }
        }
      }
    }
  }

  private nextItemIsCurrent(item, section: ReviewSection): boolean {
    const index = section.displayItems.indexOf(item);

    return index > -1 && index + 1 < section.displayItems.length && section.displayItems[index + 1].currentItem;
  }

  private updateItemPositionMap(testPartValue: TestPart) {
    this.itemPositionMap.clear();
    testPartValue.sections.forEach((section) => {
      let index = 1;
      section.items.forEach((item) => {
        this.itemPositionMap.set(item.identifier, index++);
      });
    });
  }
}
